import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreate,
  useNotify,
  SimpleForm,
  Create,
  useResourceContext,
  required,
  maxLength,
  useRefresh,
  minLength,
  AutocompleteInput,
  TextInput,
  useTranslate,
  ReferenceInput,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import RadioGroupX from "../common/RadioGroupX";
import TabForm from "../common/TabForm";
import currentDate from "../../../utils/currentDate";
import {
  // filterToQuery,
  inputText,
  matchSuggestion,
  OptionRenderer,
} from "../common/GiroCustomSelectOption";
import PickCOA from "../common/PickCOA";
import CodeNumberInput from "../../common/CodeNumberInput";

const validateNumber = [required(), minLength(14), maxLength(14)];
const validateDate = [required(), minLength(10), maxLength(10)];
const validateCustomer = [required(), minLength(36), maxLength(36)];
const validateNote = [required()];

export default function CheckCashingAddDialog({ open, setOpen }) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const t = useTranslate();

  const save = useCallback(
    async (values) => {
      try {
        await create(
          resource,
          { data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.created", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          // The shape of the returned validation errors must match the shape of the form
          return error.body.errors;
        }
      }
    },
    [create]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      fullWidth={true}
      maxWidth="md"
    >
      <Create title={" "} sx={{ "& .RaCreate-main": { mt: 0 } }}>
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>{t("mine.dialog.create_check_cashing")}</DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ alignSelf: "normal" }}>
            <CodeNumberInput
              open={open}
              dependOn="date"
              variant="outlined"
              source="code"
              label="mine.label.receipt_no"
              fullWidth
              validate={validateNumber}
              disabled
            />
            <MuiDatePickerInput
              variant="outlined"
              source="date"
              fullWidth
              isRequired
              validate={validateDate}
              defaultValue={currentDate()}
            />
            <ReferenceInput
              source="customer_debt_payment_id"
              reference="customer-debt-payments"
              perPage={10}
              sort={{ field: "customer_debt_payments.code", order: "ASC" }}
              filter={{
                "customer_debt_payments.type": 3,
                "customer_debt_payments.approved": 1,
                "CheckCashing.customer_debt_payment_id": "IS NULL",
              }}
            >
              <AutocompleteInput
                label="mine.label.check_number"
                variant="outlined"
                isRequired
                fullWidth
                validate={validateCustomer}
                optionValue="id"
                // optionText="code"
                optionText={<OptionRenderer />}
                inputText={inputText}
                matchSuggestion={matchSuggestion}
                filterToQuery={(searchText) => ({
                  "customer_debt_payments.giro": searchText,
                  "customer_debt_payments.type": 3,
                  "customer_debt_payments.approved": 1,
                  "CheckCashing.customer_debt_payment_id": "IS NULL",
                })}
              />
            </ReferenceInput>
            <RadioGroupX
              sx={{
                "& .MuiFormGroup-root": { justifyContent: "space-around" },
              }}
              choices={[
                { id: 1, name: t("mine.label.cashing") },
                { id: 2, name: t("mine.label.cancel") },
                { id: 3, name: t("mine.label.rejected") },
              ]}
            />
            <PickCOA />
            <TextInput
              variant="outlined"
              source="note"
              label="mine.label.note"
              multiline
              rows={3}
              fullWidth
              validate={validateNote}
            />

            <TabForm />
          </DialogContent>
        </SimpleForm>
      </Create>
    </Dialog>
  );
}
